import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { InviteDto } from '@ecdlink/core/src/models/dto/Invite/invite.dto';
import {
  Alert,
  renderIcon,
  BannerWrapper,
  Typography,
  Button,
  Dialog,
  DialogPosition,
  ActionModal,
} from '@ecdlink/ui';
import { useHistory } from 'react-router-dom';
import ROUTES from '@routes/routes';
import { useSelector } from 'react-redux';
import { authSelectors } from '@/store/auth';
import { PractitionerService } from '@/services/PractitionerService';
import { InviteService } from '@/services/InviteService';

interface InviteLocationState {
  inviteDto: InviteDto;
}

export const InviteDetailsPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const [removeInviteConfirmationVisible, setRemoveInviteConfirmationVisible] =
    useState<boolean>(false);

  const [programmeName, setProgrammeName] = useState<string>('Loading...');

  const userAuth = useSelector(authSelectors.getAuthUser);

  const [inviteDetails, setInviteDetails] = useState<InviteDto | null>(null);

  const callForHelp = () => {
    window.open('tel:+27800014817');
  };

  const backToClassroom = () => {
    history.push(ROUTES.CLASSROOM.ROOT);
  };

  useEffect(() => {
    if (location.state) {
      const state = location.state as InviteLocationState;
      if (state.inviteDto) {
        setInviteDetails(state.inviteDto);
      }
    }
  }, [location.state]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
    });
  };

  useEffect(() => {
    const fetchProgrammeName = async () => {
      try {
        if (userAuth && inviteDetails) {
          const practitionerService = new PractitionerService(
            userAuth.auth_token
          );
          const res =
            await practitionerService.getClassroomDetailsForPractitioner(
              inviteDetails?.principalId || ''
            );
          setProgrammeName(res?.name || 'N/A');
        }
      } catch (err) {
        console.error('Failed to fetch programme name:', err);
      }
    };

    fetchProgrammeName();
  }, [inviteDetails?.principalId, userAuth?.auth_token]);

  const removeInvite = async () => {
    if (userAuth && inviteDetails) {
      try {
        const inviteService = new InviteService(userAuth.auth_token);
        await inviteService.deleteInvite(inviteDetails.id);
        history.push(ROUTES.CLASSROOM.ROOT);
      } catch (error) {
        console.error('Failed to remove invite:', error);
      }
    }
  };

  const renderStatusAlert = (invite: InviteDto) => {
    const practitionerName = `${
      invite.practitioner?.user?.firstName || 'Practitioner'
    }`;

    let inviteDeletionDate = '';
    if (invite.insertedDate) {
      const insertedDate = new Date(invite.insertedDate);
      const deletionDate = new Date(insertedDate);
      deletionDate.setDate(insertedDate.getDate() + 30);
      inviteDeletionDate = formatDate(deletionDate);
    }

    const commonCallCentreBullet = `If ${practitionerName} needs help with Funda App, please contact the SmartStart call centre.`;
    const commonCallCentreButton = (
      <Button
        text="Contact call centre"
        icon="PhoneIcon"
        type={'filled'}
        color={'primary'}
        textColor={'white'}
        onClick={callForHelp}
      />
    );

    switch (invite.status) {
      case 'Rejected invitation':
        return (
          <Alert
            type={'error'}
            title={`${practitionerName} says they are not a practitioner at ${programmeName}. If you believe that the invite was accidentally rejected, please add the practitioner again.`}
            list={[
              commonCallCentreBullet,
              'If you added ' +
                practitionerName +
                ' by mistake, no action required.',
            ]}
            button={commonCallCentreButton}
          />
        );
      case 'Not registered on Funda App':
        return (
          <Alert
            type={'error'}
            title={`${practitionerName} has not registered on Funda App. If ${practitionerName} does not register by ${inviteDeletionDate}, this invite will be deleted.`}
            list={[commonCallCentreBullet]}
            button={commonCallCentreButton}
          />
        );
      case 'Has not joined programme':
        return (
          <Alert
            type={'error'}
            title={`${practitionerName} has not agreed to join ${programmeName} on Funda App yet. If they don't accept by ${inviteDeletionDate}, their invite will be deleted.`}
            list={[
              commonCallCentreBullet,
              `If you added ${practitionerName} by mistake, please remove them from your programme.`,
            ]}
            button={commonCallCentreButton}
          />
        );
      default:
        return (
          <Alert
            type={'info'}
            title={`Invite Status: ${invite.status}`}
            list={['No specific actions required for this status currently.']}
          />
        );
    }
  };

  return (
    <>
      <BannerWrapper
        showBackground={false}
        size="medium"
        renderBorder={true}
        title={
          inviteDetails
            ? `${inviteDetails.practitioner?.user?.firstName || ''} ${
                inviteDetails.practitioner?.user?.surname || ''
              }`
            : 'Invite Details'
        }
        color={'primary'}
        onBack={() => backToClassroom()}
        displayOffline={false}
        id={'header'}
      >
        <div className="wrapper-with-sticky-button">
          <div className="mt-4 flex flex-col gap-4"></div>
          <div className="invite-details">
            {inviteDetails ? (
              <>{renderStatusAlert(inviteDetails)}</>
            ) : (
              <p>Loading invite details...</p>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white px-4 pt-4 pb-4">
          <Button
            className={'shadow-button mt-4 w-full'}
            style={{ paddingLeft: '8px', paddingRight: '8px' }}
            color={'primary'}
            type="filled"
          >
            {renderIcon('TrashIcon', 'w-5 h-5 mr-2')}
            <Typography
              type="button"
              text={`Remove Invite`}
              color="white"
              onClick={() => setRemoveInviteConfirmationVisible(true)}
            />
          </Button>
        </div>
      </BannerWrapper>
      <Dialog
        className={'mb-16 px-4'}
        stretch={true}
        visible={removeInviteConfirmationVisible}
        position={DialogPosition.Bottom}
      >
        <ActionModal
          icon={'XCircleIcon'}
          className={''}
          iconColor="errorMain"
          iconBorderColor="errorBg"
          importantText={`Remove Invite`}
          detailText={`Are you sure you want to remove this invite? This action cannot be undone.`}
          actionButtons={[
            {
              text: 'Proceed',
              textColour: 'white',
              colour: 'primary',
              type: 'filled',
              onClick: () => removeInvite(),
              leadingIcon: 'ArrowCircleRightIcon',
            },
            {
              text: 'Close',
              textColour: 'primary',
              colour: 'primary',
              type: 'outlined',
              onClick: () => setRemoveInviteConfirmationVisible(false),
              leadingIcon: 'XIcon',
            },
          ]}
        />
      </Dialog>
    </>
  );
};
